import { Dispatch, useEffect, useState } from "react";

import { TrHover, WidgetBackground } from "@/components/colors";
import {
  getCurrencySymbol,
  GetCurrencySymbol,
} from "@/components/Dashboard/widgets/Energy";
import { EnergyIcon } from "@/components/Dashboard/widgets/Energy/icons";
import AppBadge from "@/components/utils/AppBadge";
import { ucfirst } from "@/components/utils/common";
import ContentWrapper from "@/components/utils/ContentWrapper";
import DateRangePickerWithChakra from "@/components/utils/DateRangePicker";
import GenericObjectList from "@/components/utils/GenericObjectList";
import { Loader } from "@/components/utils/Loader";
import { chartStrokeWidth } from "@/lib/constants";
import { useAppSelector } from "@/lib/hooks";
import {
  IBuilding,
  IEnergyMode,
  IUtility,
  IUtilityMonthCarbonUsage,
  IUtilityMonthUsage,
} from "@/lib/interfaces";
import {
  useAgnosticGetQuery,
  useAgnosticUntaggedGetQuery,
} from "@/lib/services/endpoints/newAPI/agnostic";
import { useGetEnergyOverTimeQuery } from "@/lib/services/endpoints/newAPI/energy";
import { OptionsType } from "@/lib/types";
import { ChevronDownIcon, ChevronRightIcon } from "@chakra-ui/icons";
import {
  Divider,
  Flex,
  FormControl,
  FormLabel,
  GridItem,
  Select,
  SimpleGrid,
  Table,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useToken,
} from "@chakra-ui/react";
import {
  addDays,
  addMinutes,
  addMonths,
  endOfDay,
  endOfMonth,
  format,
  getDaysInMonth,
  getUnixTime,
  getWeek,
  isSameMonth,
  isSameYear,
  lastDayOfMonth,
  setWeek,
  startOfMonth,
  startOfWeek,
  sub,
  subDays,
  subMonths,
} from "date-fns";
import { Formik } from "formik";
import { isEqual, set, sortBy, startCase, upperCase } from "lodash";
import { PiMoneyWavy } from "react-icons/pi";
import Switch from "react-switch";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  Brush,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { EnergyUsageWidget } from "./usage/EnergyUsageWidget";

type PlotProps = {
  fromDate?: Date;
  toDate?: Date;
  includeSummary: boolean;
  includeDatatable: boolean;
  selectedBuilding?: string;
  includeSwitch?: boolean;
  energyMode?: IEnergyMode;
  utility?: string;
  deviceRestriction?: string | undefined;
  widget?: boolean;
  defaultBuilding?: IBuilding | undefined;
  defaultClient?: string | undefined;
  utilityName?: string;
  setEstimatedValues?:
    | Dispatch<React.SetStateAction<{ weekdays: string; weekends: string }>>
    | undefined;
  setEstimatedLoading?: Dispatch<React.SetStateAction<boolean>> | undefined;
  notifyCompleted?: () => void;
  exportTableData?: () => void;
};

type EnergyMode = "cost" | "usage";

export const BoxEnergyPlot = ({
  fromDate,
  toDate,
  includeSummary,
  includeDatatable,
  energyMode,
  utility,
  deviceRestriction,
  includeSwitch = true,
  widget = false,
  selectedBuilding = "",
  defaultBuilding = undefined,
  utilityName = "",
  defaultClient = undefined,
  notifyCompleted = undefined,
  exportTableData = undefined,
  setEstimatedValues = undefined,
  setEstimatedLoading = undefined,
}: PlotProps) => {
  const [fontSizeSm] = useToken("fontSizes", ["sm"]);
  const { client: favClient } = useAppSelector(
    (state) => state.apiData.favourite
  );
  const { dateFormat } = useAppSelector((state) => state.app);
  const trHover = TrHover();
  const trDeviceBg = useColorModeValue("gray.200", "#001129");
  const trSensorBg = useColorModeValue("gray.100", "#111E34");
  const [usage, setUsage] = useState<any>([]);
  const [unitTxt, setUnitTxt] = useState<string>("");
  const [plotData, setPlotData] = useState<Array<any>>();
  const [weekDayUsage, setWeekDayUsage] = useState<string>("");
  const [weekEndUsage, setWeekEndUsage] = useState<string>("");
  const [weekDayCost, setWeekDayCost] = useState<string>("");
  const [weekEndCost, setWeekEndCost] = useState<string>("");

  const [mode, setMode] = useState<EnergyMode>("usage");

  const client = defaultClient ? defaultClient : favClient;
  const building = defaultBuilding ? defaultBuilding.id : selectedBuilding;

  const [popped, setPopped] = useState<any>({});
  const tooltipBg = WidgetBackground();

  // const now = new Date();
  // const weekStart = startOfWeek(now, { weekStartsOn: 1 })

  // const newToDate = subDays(weekStart, 1)
  // const newFromDate = subDays(weekStart, 14)

  const dateRangeLabel =
    fromDate && toDate
      ? `(${format(fromDate, dateFormat)} - ${format(toDate, dateFormat)})`
      : "";

  const { data, isLoading, isFetching } = useAgnosticUntaggedGetQuery(
    {
      url: `client/${client}/module/sensor/getUsageBreakdown`,
      params: {
        // dateFrom: getUnixTime(newFromDate),
        // dateTo: getUnixTime(newToDate),

        dateFrom: fromDate / 1000,
        dateTo: toDate / 1000 + 3600 * 24,
        building: defaultBuilding ? defaultBuilding.id : building,
        utility,
        deviceRestriction,
        wholeWeekBreakdown: 1,
      },
    },
    { skip: !client || !fromDate || !toDate || utility === "0" }
  );

  const { data: timeBreakdownRes } = useAgnosticGetQuery(
    {
      url: `client/${client}/utilityTimeBreakdowns`,
      params: {
        complete: 1,
      },
    },
    { skip: !client }
  );

  useEffect(() => {
    // if (energyMode) {
    //   if (energyMode === 'cost') { setMode('usage') }
    //   if (energyMode === 'usage') { setMode('cost') }
    // }
  }, [energyMode]);

  function compileUsages(usage, usagePeriod) {
    if (!usagePeriod) {
      usagePeriod = usage;
    }

    if (usage) {
      usage.countActive = 0;
      usage.countIdle = 0;
      usage.totalUsage = 0;
      usage.totalCost = 0;

      usage.periodUsage = 0;
      usage.periodCost = 0;

      if (usage.classes) {
        for (var cls in usage.classes) {
          var us = compileUsages(usage.classes[cls], usagePeriod);
          usage.countActive += us.countActive;
          usage.countIdle += us.countIdle;
          usage.totalUsage += us.totalUsage;
          usage.totalCost += us.totalCost;

          usage.periodUsage += us.periodUsage;
          usage.periodCost += us.periodCost;
        }
      } else if (usage.devices) {
        var dlist = [];
        for (var did in usage.devices) {
          var d = usage.devices[did];
          usage.countActive++;
          //2023-04-26: Determine idle-running devices:
          if ((100 * d.use) / (7 * usagePeriod.totalDuration) <= 10) {
            // <10% == idle
            usage.countIdle++;
          }
          usage.totalUsage += d.powerUse;
          usage.totalCost += d.cost;

          usage.periodUsage += d.periodPowerUseTotal;
          usage.periodCost += d.periodCostTotal;

          dlist.push(d);
        }
        dlist.sort(function (a, b) {
          return b.powerUse - a.powerUse;
        });
        usage.devices = dlist;
      }
    }
    return usage;
  }

  const getCostMultiplier = (dayIdx, minute, utility, tariff) => {
    if (tariff) {
      if (tariff.schedule) {
        var ds = tariff.schedule[dayIdx];
        if (ds) {
          for (var i = 0; i < ds.length; i++) {
            var p = ds[i];
            if (minute >= p.startTime && minute <= p.endTime) {
              return +p.unitCost;
            }
          }
        }
      }
      return +tariff.baseUnitCost;
    }
    return +utility.unitCost;
  };

  useEffect(() => {
    if (setEstimatedValues) {
      setEstimatedValues({
        weekdays: weekDayUsage,
        weekends: weekEndUsage,
      });
    }
  }, [weekDayUsage, weekEndUsage, setEstimatedValues]);

  useEffect(() => {
    if (data && timeBreakdownRes) {
      if (data.status == "success" && timeBreakdownRes.status == "success") {
        //2024-07-19: Need to compute number of days of each category are a part of averages:
        let weekendDays = 0;
        let weekdayDays = 0;

        if (+fromDate && +toDate) {
          //Should match query:
          let dow = fromDate.getUTCDay(); //0 = sunday
          const nDays =
            1 + Math.round(toDate / 1000 - fromDate / 1000) / (3600 * 24);

          //Could just do a divide(!), but doing this the simple way:
          for (let i = 0; i < nDays; i++) {
            if (dow == 0 || dow == 6) {
              weekendDays++;
            } else {
              weekdayDays++;
            }
            dow = (dow + 1) % 7;
          }
        }
        //****

        const utility: OptionsType[] = data?.utility;

        let bdd = [...timeBreakdownRes.data];
        if (
          building != "" &&
          building &&
          bdd.find((tbd) => tbd.building == building)
        ) {
          bdd = bdd.filter((tbd) => tbd.building == building);
        } else {
          bdd = bdd.filter((tbd) => tbd.building == "0");
        }

        bdd.sort((a, b) => {
          return Number(a.periodStartTime) < Number(b.periodStartTime) ? -1 : 1;
        });

        const periodsOfUse = [];
        bdd.forEach((tb) => {
          const start = +tb.periodStartTime / 60;
          if (periodsOfUse.length) {
            periodsOfUse[periodsOfUse.length - 1].to = start;
          }
          periodsOfUse.push({ from: start, to: 24, name: tb.periodName });
        });

        //Separately compute total duration:
        for (var i = 0; i < periodsOfUse.length; i++) {
          var u = periodsOfUse[i];
          u.totalDuration = u.to - u.from;
        }
        if (periodsOfUse.length > 1 && periodsOfUse[0].from > 0) {
          periodsOfUse[periodsOfUse.length - 1].totalDuration +=
            periodsOfUse[0].from;
        }

        var usage = {
          weekday: { approx: 0, trace: [], costApprox: 0, costTrace: [] },
          weekend: { approx: 0, trace: [], costApprox: 0, costTrace: [] },
          periodUsage: periodsOfUse,
        };

        for (var i = 0; i < 24; i++) {
          usage.weekday.trace[i] = 0;
          usage.weekend.trace[i] = 0;
          usage.weekday.costTrace[i] = 0;
          usage.weekend.costTrace[i] = 0;
        }

        for (var i = 0; i < usage.periodUsage.length; i++) {
          usage.periodUsage[i].classes = {};
          usage.periodUsage[i].countActive = 0;
          usage.periodUsage[i].countIdle = 0;
          usage.periodUsage[i].totalUsage = 0;
          usage.periodUsage[i].totalCost = 0;
        }

        //Compute energy usage:
        var dcus = data.deviceClassUsages;
        for (var deviceClassId in dcus) {
          for (var pu = 0; pu < usage.periodUsage.length; pu++) {
            if (!usage.periodUsage[pu].classes[deviceClassId]) {
              usage.periodUsage[pu].classes[deviceClassId] = {
                deviceClass: dcus[deviceClassId].deviceClass,
                devices: {},
              };
            }
          }

          //id, kind, devices
          var devices = dcus[deviceClassId].devices;
          for (var j = 0; j < devices.length; j++) {
            var d = devices[j];
            //id, name, powerRating, usage
            //   usage->active, cooling, heating

            for (var h = 0; h < 24 * 7; h++) {
              //h is now hour in week
              var t = h % 24;
              if (h < 24 || h >= 24 * 6) {
                t += 24;
              }
              //t<24 = weekday, t>=24 = weekend
              const isWeekend = t >= 24;

              //Clamp 0 - 1
              var activeProp = 0;

              for (var usageType in d.usage) {
                //Just coalesce:
                activeProp += Number(d.usage[usageType][h].a);
              }
              //2024-07-24: Energy consumption profile now not assumed, applied by server.
              activeProp = Math.max(0, Math.min(100, +activeProp));

              var key = t < 24 ? "weekday" : "weekend";
              var use = activeProp / 100;
              var powerUse = use * d.powerRating;

              var cost =
                powerUse *
                getCostMultiplier(
                  Math.floor(h / 24),
                  (h % 24) * 60,
                  data.utility,
                  data.tariffByBuildingAtEndOfPeriod[d.bid]
                );

              var useDivisor =
                t < 24 ? Math.min(5, weekdayDays) : Math.min(2, weekendDays); //make per-day in the plot
              //2024-07-29: Changed to handle the case where period < 1 week

              if (useDivisor == 0) {
                useDivisor = 1;
              } //Prevent NaNs; numerator should be 0.

              usage[key].approx += powerUse / useDivisor;
              usage[key].trace[t % 24] += powerUse / useDivisor;
              usage[key].costApprox += cost / useDivisor;
              usage[key].costTrace[t % 24] += cost / useDivisor;

              //For tabulating:
              if (use > 0.01) {
                var tGlobal = t % 24;

                for (var pu = 0; pu < usage.periodUsage.length; pu++) {
                  var group = usage.periodUsage[pu];
                  if (
                    (tGlobal >= group.from && tGlobal < group.to) ||
                    pu == usage.periodUsage.length - 1
                  ) {
                    //Second case here is for final group, if no prior match
                    if (!group.classes[deviceClassId].devices[d.id]) {
                      group.classes[deviceClassId].devices[d.id] = {
                        device: d,
                        use: 0,
                        powerUse: 0,
                        cost: 0,

                        //2024-07-19 - reconstructed value for period, with excessive breakdown
                        periodUseWeekday: 0,
                        periodPowerUseWeekday: 0,
                        periodCostWeekday: 0,
                        periodUseWeekend: 0,
                        periodPowerUseWeekend: 0,
                        periodCostWeekend: 0,
                        periodUseTotal: 0,
                        periodPowerUseTotal: 0,
                        periodCostTotal: 0,
                      };
                    }
                    group.classes[deviceClassId].devices[d.id].use += use;
                    group.classes[deviceClassId].devices[d.id].powerUse +=
                      powerUse;
                    group.classes[deviceClassId].devices[d.id].cost += cost;

                    //2024-07-19: Added periodUse metrics:
                    const periodMultiplier =
                      (isWeekend ? weekendDays : weekdayDays) / useDivisor;
                    //Extra breakdown:
                    if (isWeekend) {
                      group.classes[deviceClassId].devices[
                        d.id
                      ].periodUseWeekend += use * periodMultiplier;
                      group.classes[deviceClassId].devices[
                        d.id
                      ].periodPowerUseWeekend += powerUse * periodMultiplier;
                      group.classes[deviceClassId].devices[
                        d.id
                      ].periodCostWeekend += cost * periodMultiplier;
                    } else {
                      group.classes[deviceClassId].devices[
                        d.id
                      ].periodUseWeekday += use * periodMultiplier;
                      group.classes[deviceClassId].devices[
                        d.id
                      ].periodPowerUseWeekday += powerUse * periodMultiplier;
                      group.classes[deviceClassId].devices[
                        d.id
                      ].periodCostWeekday += cost * periodMultiplier;
                    }
                    group.classes[deviceClassId].devices[d.id].periodUseTotal +=
                      use * periodMultiplier;
                    group.classes[deviceClassId].devices[
                      d.id
                    ].periodPowerUseTotal += powerUse * periodMultiplier;
                    group.classes[deviceClassId].devices[
                      d.id
                    ].periodCostTotal += cost * periodMultiplier;
                    //****/

                    break; //Only in one group
                  }
                }
              }
            }
          }
        }

        for (var pu = 0; pu < usage.periodUsage.length; pu++) {
          compileUsages(usage.periodUsage[pu]);
        }

        const traceKey = mode === "usage" ? "trace" : "costTrace";

        const pd = [];
        for (var i = 0; i < 24; i++) {
          var pdo = {
            time: i + ":00",
            weekend: usage.weekend[traceKey][i],
            weekday: usage.weekday[traceKey][i],
          };
          pd.push(pdo);
        }
        pd.push({
          time: "24:00",
          weekend: usage.weekend[traceKey][0],
          weekday: usage.weekday[traceKey][0],
        });
        setPlotData(pd);

        setWeekDayUsage(
          (usage.weekday.approx * 5).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        );
        // }) + " " + utility.unitDescription);
        setWeekEndUsage(
          (usage.weekend.approx * 2).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        );
        // }) + " " + utility.unitDescription);

        setWeekDayCost(
          (usage.weekday.costApprox * 5).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        );
        setWeekEndCost(
          (usage.weekend.costApprox * 2).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        );

        setUsage(usage);
        setUnitTxt(utility.unitDescription);

        setEstimatedLoading && setEstimatedLoading(false);

        //Functions for use in report render:
        if (notifyCompleted) {
          notifyCompleted(true);
        }
      }
    }
  }, [
    data,
    mode,
    timeBreakdownRes,
    building,
    notifyCompleted,
    fromDate,
    toDate,
  ]);

  useEffect(() => {
    if (exportTableData) {
      const rows = [];
      const criticalRows = [];

      usage?.periodUsage?.forEach((u) => {
        //By period:
        const row = {
          period: u.name,
          deviceType: "",
          deviceName: "",
          active: u.countActive,
          usage: Number(u.totalUsage).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }),
          cost: Number(u.totalCost).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }),
        };
        rows.push(row);
        criticalRows.push(row);

        //By type:
        Object.keys(u.classes).forEach((key) => {
          const dc = u.classes[key];

          if (!dc.devices?.length) {
            //No class line if no devices
            return;
          }

          const row = {
            period: "",
            deviceType: dc.deviceClass,
            deviceName: "",
            active: dc.countActive,
            usage: Number(dc.totalUsage).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }),
            cost: Number(dc.totalCost).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }),
          };
          rows.push(row);
          criticalRows.push(row);

          var nonCriticalUsage = 0;
          var nonCriticalCost = 0;

          //By device:
          dc.devices.map((device) => {
            const nDays =
              1 + Math.round(toDate / 1000 - fromDate / 1000) / (3600 * 24);
            const percUse =
              (100 * device.use) / (Math.min(nDays, 7) * u.totalDuration);
            //2024-07-29:
            //Total duration is time in period in one day.
            //Device use is average weekly use for device, in queried period

            const row = {
              period: "",
              deviceType: "",
              deviceName: device.device?.name,
              active: Number(percUse).toFixed(2) + "% active",
              usage: Number(device.powerUse).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
              cost: Number(device.cost).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
            };
            rows.push(row);
            if (percUse >= 75) {
              criticalRows.push(row);
            } else {
              nonCriticalUsage += Number(device.powerUse);
              nonCriticalCost += Number(device.cost);
            }
          });

          //Add sum of others:
          if (nonCriticalUsage) {
            const row = {
              period: "",
              deviceType: "",
              deviceName: "Other devices (sub-75% utilisation)",
              active: "",
              usage: Number(nonCriticalUsage).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
              cost: Number(nonCriticalCost).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
            };
            criticalRows.push(row);
          }
        });
      });

      if (deviceRestriction == "majorPlant") {
        exportTableData("majorPlantPeriodUsage", rows);
        exportTableData("majorPlantCriticalPeriodUsage", criticalRows);
      } else {
        exportTableData("periodUsage", rows);
        exportTableData("criticalPeriodUsage", criticalRows);
      }
    }
  }, [exportTableData, usage, deviceRestriction]);

  const togglePopped = (key) => {
    const newPopped = { ...popped };
    newPopped[key] = !newPopped[key];
    setPopped(newPopped);
  };

  const ArrowDown = () => <ChevronDownIcon w="20px" h="20px" mr={1} />;
  const ArrowRight = () => <ChevronRightIcon w="20px" h="20px" mr={1} />;

  return (
    <>
      {isLoading || isFetching ? (
        <Loader />
      ) : utility === "" || utility === "0" ? (
        <Flex justifyContent="center" padding={5}>
          Please select utility
        </Flex>
      ) : (
        <>
          <Flex direction={"column"} mt={widget ? 2 : 5} w="full">
            <Flex direction={"column"}>
              <Flex justifyContent={"space-between"}>
                <GraphTitle
                  text={`24h ${deviceRestriction === "majorPlant" ? "Major Plant" : ""} Average ${ucfirst(utilityName)} Usage ${dateRangeLabel}`}
                  widget={widget}
                />
              </Flex>
              {includeSwitch ? (
                <Flex gap={2}>
                  <Text fontSize={"sm"} mr={1}>
                    Change mode{" "}
                  </Text>
                  <Switch
                    checked={mode !== "usage" ? true : false}
                    onChange={() => {
                      if (mode === "cost") {
                        setMode("usage");
                      } else {
                        setMode("cost");
                      }
                    }}
                    handleDiameter={22}
                    offColor="#ECEEEE"
                    onColor="#ECEEEE"
                    height={28}
                    width={56}
                    activeBoxShadow="0px 0px 5px 5px lightgray"
                    uncheckedIcon={<PiMoneyWavy size={18} fill="black" />}
                    // uncheckedIcon={<Flex h='100%' alignItems={'center'} justifyContent={'center'}><DollarIcon fill='gray' /></Flex>}
                    checkedIcon={
                      <Flex
                        h="100%"
                        alignItems={"center"}
                        justifyContent={"center"}
                      >
                        <EnergyIcon fill="gray" />
                      </Flex>
                    }
                    uncheckedHandleIcon={
                      <Flex
                        h="100%"
                        alignItems={"center"}
                        justifyContent={"center"}
                      >
                        <EnergyIcon fill="black" />
                      </Flex>
                    }
                    checkedHandleIcon={<PiMoneyWavy size={18} fill="gray" />}
                    // checkedHandleIcon={<Flex h='100%' alignItems={'center'} justifyContent={'center'}><DollarIcon fill='black' /></Flex>}
                  />
                </Flex>
              ) : (
                <></>
              )}
            </Flex>
            <ResponsiveContainer width="100%" height={widget ? "100%" : 400}>
              <AreaChart
                data={plotData}
                margin={{
                  top: 20,
                  right: 20,
                  left: 40,
                  bottom: 20,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.5} />

                <defs>
                  <linearGradient id="weekday" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#FFCD41" stopOpacity={0.5} />
                    <stop offset="95%" stopColor="#FFCD41" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="weekend" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ED8936" stopOpacity={0.5} />
                    <stop offset="95%" stopColor="#ED8936" stopOpacity={0} />
                  </linearGradient>
                </defs>

                <Area
                  isAnimationActive={false}
                  type="monotone"
                  dataKey={"weekday"}
                  stroke="#FFCD41"
                  fillOpacity={1}
                  fill="url(#weekday)"
                />
                <Area
                  isAnimationActive={false}
                  type="monotone"
                  dataKey={"weekend"}
                  stroke="#ED8936"
                  fillOpacity={1}
                  fill="url(#weekend)"
                />

                <YAxis
                  fontSize={fontSizeSm}
                  unit={
                    mode === "usage" ? unitTxt : getCurrencySymbol(building)
                  }
                  type="number"
                  // domain={[(dataMin) => 0, (dataMax) => Math.floor(dataMax * 1.2)]}

                  orientation="left"
                  interval={"preserveStart"}
                  tickFormatter={(value) => {
                    const underSettings = {
                      maximumFractionDigits: 2,
                      minimumFractionDigits: 2,
                    };
                    const overSettings = {
                      maximumFractionDigits: 0,
                    };

                    return Number(value).toLocaleString(
                      "en-US",
                      value < 5 ? underSettings : overSettings
                    );
                  }}
                />

                <XAxis
                  fontSize={fontSizeSm}
                  tickSize={10}
                  tickMargin={5}
                  dataKey="time"
                  interval={"preserveStart"}
                />

                <Legend
                  wrapperStyle={{ fontSize: fontSizeSm }}
                  verticalAlign="top"
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: tooltipBg,
                    borderRadius: "5px",
                    fontSize: fontSizeSm,
                  }}
                  offset={10}
                  formatter={(v) => {
                    try {
                      const value = Number(v).toLocaleString("en-GB", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      });
                      return mode == "cost"
                        ? `£ ${value}`
                        : `${value} ${unitTxt}`;
                    } catch (e) {
                      return v;
                    }
                  }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </Flex>
          {includeSummary ? (
            <Flex direction={"column"} alignItems={"center"}>
              <Text fontSize={"sm"} fontWeight={500}>
                Estimated Weekly
                {mode === "cost" ? " Cost" : " Usage"}
              </Text>
              <Flex direction={"column"}>
                {mode === "cost" ? (
                  <>
                    <Text fontSize={"sm"}>£{weekDayCost} (weekdays) </Text>
                    <Text fontSize={"sm"}>£{weekEndCost} (weekends)</Text>
                  </>
                ) : (
                  <>
                    <Text fontSize={"sm"}>{weekDayUsage} (weekdays) kWh</Text>
                    <Text fontSize={"sm"}>{weekEndUsage} (weekends) kWh</Text>
                  </>
                )}
              </Flex>
            </Flex>
          ) : (
            <></>
          )}
          {includeDatatable ? (
            <>
              <GraphTitle
                text={`Approximate Usage in Period ${dateRangeLabel}`}
                widget={widget}
              />
              <Table
                size="sm"
                style={{ marginTop: "32px", borderCollapse: "separate" }}
              >
                <Thead>
                  <Tr>
                    <Th rowSpan={2}>Period</Th>
                    <Th rowSpan={2}>Devices Active</Th>
                    <Th rowSpan={2} isNumeric={true}>
                      Estimated
                      <br />
                      Weekly Usage
                      {mode === "cost" ? " (£)" : " (kWh)"}
                    </Th>
                    <Th colSpan={3} style={{ textAlign: "center" }}>
                      Period Usage
                      {mode === "cost" ? " (£)" : " (kWh)"}
                    </Th>
                  </Tr>
                  <Tr>
                    <Th isNumeric={true}>Weekday</Th>
                    <Th isNumeric={true}>Weekend</Th>
                    <Th isNumeric={true}>Total</Th>
                  </Tr>
                </Thead>
                {usage?.periodUsage?.map((u, periodIndex) => {
                  return (
                    <>
                      <Tr
                        transition={"all 0.2s ease-in-out"}
                        _hover={{ bg: trHover }}
                        cursor={"pointer"}
                        onClick={() => {
                          togglePopped(periodIndex);
                        }}
                        key={periodIndex}
                      >
                        <Td>
                          {popped[periodIndex] ? <ArrowDown /> : <ArrowRight />}
                          {u.name}
                        </Td>

                        <Td>
                          <AppBadge
                            status="gray"
                            label={u.countActive - u.countIdle}
                          />
                        </Td>
                        <Td isNumeric>
                          {Number(
                            u[mode === "usage" ? "totalUsage" : "totalCost"]
                          ).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </Td>
                        <Td colSpan={3} isNumeric>
                          {Number(
                            u[mode === "usage" ? "periodUsage" : "periodCost"]
                          ).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </Td>
                      </Tr>

                      {popped[periodIndex] ? (
                        Object.keys(u.classes).map((key) => {
                          const dc = u.classes[key];
                          var rowKey = periodIndex + "-" + dc.deviceClass;
                          if (dc.countActive === 0) return <></>;
                          return (
                            <>
                              <Tr
                                key={rowKey}
                                bg={trDeviceBg}
                                transition={"all 0.2s ease-in-out"}
                                _hover={{ bg: trHover }}
                                cursor={"pointer"}
                                onClick={() => {
                                  togglePopped(rowKey);
                                }}
                              >
                                <Td textIndent={"20px"}>
                                  {popped[rowKey] ? (
                                    <ArrowDown />
                                  ) : (
                                    <ArrowRight />
                                  )}
                                  {dc.deviceClass}
                                </Td>
                                <Td>
                                  <AppBadge
                                    status="gray"
                                    label={dc.countActive - dc.countIdle}
                                  />
                                </Td>
                                <Td isNumeric>
                                  {Number(
                                    dc[
                                      mode === "usage"
                                        ? "totalUsage"
                                        : "totalCost"
                                    ]
                                  ).toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </Td>
                                <Td colSpan={3} isNumeric>
                                  {Number(
                                    dc[
                                      mode === "usage"
                                        ? "periodUsage"
                                        : "periodCost"
                                    ]
                                  ).toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </Td>
                              </Tr>
                              {popped[rowKey] ? (
                                dc.devices.map((device, didx) => {
                                  const nDays =
                                    1 +
                                    Math.round(
                                      toDate / 1000 - fromDate / 1000
                                    ) /
                                      (3600 * 24);
                                  const percUse =
                                    (100 * device.use) /
                                    (Math.min(nDays, 7) * u.totalDuration);
                                  //2024-07-29:
                                  //Total duration is time in period in one day.
                                  //Device use is average weekly use for device, in queried period

                                  return (
                                    <Tr
                                      // transform={'translateX(40px)'}
                                      key={rowKey + "-" + didx}
                                      bg={trSensorBg}
                                      transition={"all 0.2s ease-in-out"}
                                      cursor={"pointer"}
                                    >
                                      <Td textIndent={"40px"}>
                                        {device.device?.name}
                                      </Td>
                                      <Td>
                                        <AppBadge
                                          status={
                                            percUse > 75
                                              ? "error"
                                              : percUse > 25
                                                ? "warning"
                                                : "success"
                                          }
                                          label={
                                            Number(percUse).toFixed(2) +
                                            "% power utilisation"
                                          }
                                        />
                                      </Td>
                                      <Td isNumeric>
                                        {Number(
                                          mode === "usage"
                                            ? device.powerUse
                                            : device.cost
                                        ).toLocaleString(undefined, {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        })}
                                      </Td>

                                      <Td isNumeric>
                                        {Number(
                                          mode === "usage"
                                            ? device.periodPowerUseWeekday
                                            : device.periodCostWeekday
                                        ).toLocaleString(undefined, {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        })}
                                      </Td>
                                      <Td isNumeric>
                                        {Number(
                                          mode === "usage"
                                            ? device.periodPowerUseWeekend
                                            : device.periodCostWeekend
                                        ).toLocaleString(undefined, {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        })}
                                      </Td>
                                      <Td isNumeric>
                                        {Number(
                                          mode === "usage"
                                            ? device.periodPowerUseTotal
                                            : device.periodCostTotal
                                        ).toLocaleString(undefined, {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        })}
                                      </Td>
                                    </Tr>
                                  );
                                })
                              ) : (
                                <></>
                              )}
                            </>
                          );
                        })
                      ) : (
                        <></>
                      )}
                    </>
                  );
                })}
              </Table>
            </>
          ) : (
            <></>
          )}
        </>
      )}
    </>
  );
};

//////////
// Wrapper with additional content and controls (as used on page)
//////////

type Props = {
  includeSummary: boolean;
  includeDatatable: boolean;
  pickDates?: boolean;
  initialFromDate?: Date;
  initialToDate?: Date;
  utilities: IUtility[] | undefined;
  carbonFootprint?: boolean;
  deviceUtilization?: boolean;
};

const BoxEnergyModule = ({
  includeSummary,
  pickDates,
  initialFromDate,
  initialToDate,
  includeDatatable,
  utilities = [],
  carbonFootprint = false,
  deviceUtilization = false,
}: Props) => {
  const [fromDate, setFromDate] = useState<Date | undefined>(initialFromDate);
  const { building } = useAppSelector((s) => s.apiData.favourite);
  const { buildings } = useAppSelector((s) => s.apiData);
  const [toDate, setToDate] = useState<Date | undefined>(initialToDate);
  const [utility, setUtility] = useState<IUtility | undefined>(undefined);
  //
  const [includeOutsideTemp, setIncludeOutsideTemp] = useState(0);
  const [compare, setCompare] = useState(false);
  const findBuilding = buildings.find((b) => b.id === building);

  useEffect(() => {
    setFromDate(initialFromDate);
    setToDate(initialToDate);
  }, [initialFromDate, initialToDate]);

  useEffect(() => {
    //set first available default utility from array of utilities
    if (utilities.length > 0) {
      setUtility(utilities[0]);
    }
  }, [utilities]);
  if (utility === undefined) {
    return <></>;
  }
  return (
    <>
      <>
        {pickDates ? (
          <ContentWrapper>
            <Formik
              validateOnMount={true}
              enableReinitialize={true}
              initialValues={{
                fromDate: fromDate / 1000,
                toDate: toDate / 1000,
                utility: utility,
              }}
              onSubmit={(values, { setSubmitting }) => {
                setFromDate(new Date(values.fromDate * 1000));
                setToDate(new Date(values.toDate * 1000));
                setUtility(values.utility);
                setSubmitting(false);
              }}
            >
              {({ handleSubmit, values, ...props }) => {
                return (
                  <>
                    <form onSubmit={handleSubmit} noValidate>
                      <SimpleGrid
                        columns={[1, 1, 1, 3]}
                        spacingX={4}
                        spacingY={2}
                      >
                        <GridItem>
                          <DateRangePickerWithChakra
                            width="full"
                            maxDate={addDays(new Date(), 1)}
                            label="Date range:"
                            startDate={fromDate}
                            setStartDate={setFromDate}
                            endDate={toDate}
                            setEndDate={setToDate}
                          />
                        </GridItem>

                        <GridItem>
                          <FormControl id={"utility"}>
                            <FormLabel htmlFor={"utility"}>Utility:</FormLabel>
                            <Select
                              value={utility.id}
                              onChange={(
                                e: React.ChangeEvent<HTMLSelectElement>
                              ) => {
                                setUtility(
                                  utilities.find(
                                    (item) => item.id == e.target.value
                                  )
                                );
                              }}
                            >
                              <option value="">Please select utility</option>
                              {utilities.map(({ id, name }) => {
                                return (
                                  <option key={id} value={id}>
                                    {name}
                                  </option>
                                );
                              })}
                            </Select>
                          </FormControl>
                        </GridItem>
                      </SimpleGrid>
                    </form>
                  </>
                );
              }}
            </Formik>
          </ContentWrapper>
        ) : (
          <></>
        )}
      </>

      {!carbonFootprint ? (
        <>
          {!deviceUtilization && (
            <>
              <BoxEnergyOverTimePlot
                fromDate={fromDate}
                toDate={toDate}
                utility={utility ? utility.id! : ""}
                utilityName={utility.name}
                selectedBuilding={building}
                unit={utility.unitDescription}
              />
              {/* <EnergyUsageWidget
                dateRange="daily"
                type="singleBuilding"
                includeOutsideTemp={includeOutsideTemp}
                hideBuildingPicker={building === undefined ? true : false}
                pollingInterval={0}
                building={findBuilding}
                error={false}
                fromDate={fromDate}
                toDate={toDate}
                utilityType={utility.type}
                mode={"landscape"}
                widget={undefined}
                buildings={[building]}
                layout={[]}
                compare={compare}
                setCompare={setCompare}
                setLayout={() => {}}
                setBuilding={() => {}}
                setFromDate={setFromDate}
                setToDate={setToDate}
                setIncludeOutsideTemp={setIncludeOutsideTemp}
                hideModuleLink={false}
                chartOnly={false}
              /> */}
              <Divider mt={5} />
            </>
          )}
          {!deviceUtilization && (
            <UtilityUsageOverMonth
              utility={utility ? utility.id! : ""}
              utilityName={utility.name}
              selectedBuilding={building}
              unit={utility.unitDescription}
            />
          )}
          <Divider my={5} />
          <BoxEnergyPlot
            fromDate={fromDate}
            toDate={toDate}
            includeSummary={includeSummary}
            includeDatatable={includeDatatable}
            selectedBuilding={building}
            utility={utility.id}
            utilityName={utility.name}
          />
        </>
      ) : null}
      {carbonFootprint ? (
        <>
          <Divider my={5} />
          <CarbonFootprintChart
            selectedBuilding={building}
            utility={utility.id}
            unit={utility.unitDescription}
            kgCo2={+utility.kgCo2}
          />
          <Divider my={5} />
          <CarbonFootprintTargetChart utility={utility} building={building} />
        </>
      ) : null}
    </>
  );
};

type BoxEnergyOverTimePlotProps = {
  fromDate: Date | undefined;
  toDate: Date | undefined;
  utility: string;
  selectedBuilding: string;
  unit: string;
  widget?: boolean;
  card?: boolean;
  utilityName?: string;
  defaultClient: string;
  defaultBuilding: string;
  energyMode?: IEnergyMode | undefined;
  mode: "daily" | "weekly";
  chartType: "area" | "bar";
  setQueryLoading?: React.Dispatch<React.SetStateAction<boolean>> | undefined;
  notifyCompleted?: () => void;
};

export const BoxEnergyOverTimePlot = ({
  mode = "daily",
  fromDate,
  toDate,
  utility,
  unit,
  selectedBuilding = "",
  utilityName = "",
  defaultClient = "",
  defaultBuilding = "",
  energyMode = undefined,
  widget = false,
  chartType = "area",
  card = false,
  asset = undefined,
  setQueryLoading = undefined,
  notifyCompleted,
}: BoxEnergyOverTimePlotProps) => {
  const [fontSize] = useToken("fontSizes", ["md"]);
  const [fontSizeSm] = useToken("fontSizes", ["sm"]);
  const { client: favClient } = useAppSelector(
    (state) => state.apiData.favourite
  );
  const { dateFormat: rdxDateFormat } = useAppSelector((state) => state.app);
  const [data, setData] = useState<Array<{ time: string; Usage: number }>>([]);
  const tooltipBg = WidgetBackground();
  const client = defaultClient ? defaultClient : favClient;
  const building = defaultBuilding ? defaultBuilding : selectedBuilding;

  let dateFormat = rdxDateFormat;
  if (card) {
    dateFormat = "dd/MM";
  }
  const filters = {
    utility,
    building,
    dateFrom: getUnixTime(fromDate!),
    dateTo: getUnixTime(toDate!),
  };

  if (asset) {
    filters["asset"] = asset.id;
  }

  const {
    data: energyData,
    isLoading,
    isFetching,
  } = useGetEnergyOverTimeQuery(
    {
      client,
      filters,
    },
    {
      skip: fromDate === undefined || toDate === undefined,
    }
  );

  useEffect(() => {
    const dataType = energyMode == "cost" ? "costByDate" : "usageByDate";

    if (!isLoading && !isFetching && energyData?.status === "success") {
      setQueryLoading && setQueryLoading(false);
      setData([]);

      if (mode === "daily") {
        try {
          Object.keys(energyData[dataType]).map((key) => {
            setData((prevState) => [
              ...prevState,
              { time: key, Usage: energyData[dataType][key] },
            ]);
          });
        } catch (e) {}
      }

      if (mode === "weekly") {
        const weeklyData = [];

        Object.keys(energyData[dataType]).map((key) => {
          const weekNo = getWeek(new Date(+key * 1000));
          if (weeklyData[weekNo] === undefined) {
            weeklyData[weekNo] = energyData[dataType][key];
          } else {
            weeklyData[weekNo] += energyData[dataType][key];
          }
        });

        weeklyData.map((total, weekNo) => {
          const d = startOfWeek(
            setWeek(new Date(), weekNo, {
              // weekStartsOn: 1
            }),
            { weekStartsOn: 1 }
          );
          setData((prevState) => [
            ...prevState,
            { time: getUnixTime(d), Usage: total },
          ]);
        });
      }

      if (notifyCompleted) {
        notifyCompleted();
      }
    }
  }, [
    energyData,
    isLoading,
    isFetching,
    energyMode,
    building,
    mode,
    notifyCompleted,
  ]);

  if (utility === "") {
    return <></>;
  }
  if (isLoading || isFetching) {
    return <Loader />;
  }

  const chartTooltip = (
    <Tooltip
      wrapperStyle={{
        fontSize,
      }}
      cursor={{ fill: "transparent" }}
      contentStyle={{ backgroundColor: tooltipBg, borderRadius: "5px" }}
      offset={10}
      labelFormatter={(timeStr, arr) => {
        if (!arr || !arr[0]) return "";
        return `Date: ${format(new Date(arr[0].payload.time * 1000), `E ${dateFormat}`)}`;
      }}
      formatter={(value, name) => {
        try {
          const v = +value;

          return (
            Number(v).toLocaleString("en-US", {
              maximumFractionDigits: 2,
              minimumFractionDigits: 2,
            }) +
            " " +
            unit
          );
        } catch (e) {
          return value;
        }
      }}
    />
  );

  const yAxis = (
    <YAxis
      // fontSize={card ? fontSizeSm : "auto"}
      fontSize={fontSizeSm}
      unit={unit}
      dataKey={"Usage"}
      type="number"
      domain={[
        (_dataMin: number) => 0,
        (dataMax: number) => dataMax + dataMax * 0.05,
      ]}
      orientation="left"
      tickFormatter={(value) => {
        if (value === 0) {
          return "0";
        }
        return Number(value).toLocaleString("en-US", {
          maximumFractionDigits: 0,
        });
      }}
    ></YAxis>
  );

  const xAxis = (
    <XAxis
      fontSize={fontSizeSm}
      tickSize={10}
      tickMargin={5}
      dataKey="time"
      interval={"preserveStartEnd"}
      tickFormatter={(timeStr) => {
        try {
          const date = new Date(timeStr * 1000);
          if (mode === "daily") {
            return format(date, dateFormat);
          }
          if (mode === "weekly") {
            return (
              format(subDays(date, 7), "dd") +
              "-" +
              format(subDays(date, 1), dateFormat)
            );
          }
        } catch (e) {
          return timeStr;
        }
      }}
    />
  );

  const brush = !widget ? (
    <Brush
      style={{ marginTop: "220px" }}
      tickFormatter={(timeStr) => {
        return format(new Date(timeStr * 1000), `${dateFormat} HH:mm`);
      }}
      dataKey="time"
      height={30}
    />
  ) : (
    <></>
  );

  const grid = <CartesianGrid strokeDasharray="5 5" />;

  return (
    <Flex w="full" direction="column" p={widget ? 3 : 0}>
      {!widget ? (
        <GraphTitle text={`Daily ${utilityName} Usage`} widget={widget} />
      ) : (
        <></>
      )}
      <ResponsiveContainer width="100%" height={widget ? "100%" : 400}>
        {chartType === "area" ? (
          <AreaChart data={data} margin={{ left: 40 }}>
            <defs>
              <linearGradient id="energyWave" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#FFCD41" stopOpacity={1} />
                <stop offset="95%" stopColor="#FFCD41" stopOpacity={0} />
              </linearGradient>
            </defs>
            <Area
              strokeWidth={chartStrokeWidth}
              fill="url(#energyWave)"
              type="monotone"
              dataKey="Usage"
              stroke="#FFD400"
              dot={false}
            />
            {chartTooltip}
            {yAxis}
            {xAxis}
            {brush}
            {grid}
          </AreaChart>
        ) : (
          <BarChart data={data} margin={{ left: 40 }}>
            <defs>
              <linearGradient id="energyWave" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#FFCD41" stopOpacity={1} />
                <stop offset="95%" stopColor="#FFCD41" stopOpacity={0} />
              </linearGradient>
            </defs>
            <Bar
              fill="url(#energyWave)"
              type="monotone"
              dataKey="Usage"
              stroke="#FFD400"
            />
            <CartesianGrid strokeDasharray="5 5" />
            {chartTooltip}
            {yAxis}
            {xAxis}
            {brush}
            {grid}
          </BarChart>
        )}
      </ResponsiveContainer>
    </Flex>
  );
};

//Created as a separate component so we can replace if necessary; should be sufficient.
// e.g. type = device/deviceClass, ownerId is the id of a device/class

export const UtilityUsageListing = ({ type, ownerId }) => {
  const { client } = useAppSelector((state) => state.apiData.favourite);

  return client ? (
    <GenericObjectList
      page="utilityUsageListing"
      apiRoot={`client/${client}/utilityUsage`}
      apiParams={{
        filters: [{ field: type, value: ownerId }],
      }}
      allowCreate={true}
      columnMutator={(cols) => {
        return cols.map((c) => {
          if (c.accessor == type) {
            c.defaultValue = ownerId;
          }
          return c;
        });
      }}
    />
  ) : (
    <>{"To configure utility usage, please first select client"}</>
  );
};

export default BoxEnergyModule;

type GraphTitleProps = {
  widget: boolean;
  text: string;
};

export const GraphTitle = ({ widget, text }: GraphTitleProps) => {
  return (
    <Text
      fontSize={"md"}
      whiteSpace="nowrap"
      py={widget ? 0 : 5}
      fontWeight={500}
    >
      {text}{" "}
    </Text>
  );
};

type UtilityUsageOverMonthProps = {
  utility: string;
  selectedBuilding: string;
  unit: string;
  widget?: boolean;
  utilityName?: string;
  defaultClient?: string;
  defaultBuilding?: string;
  queryData: any;
  setQueryLoading?: React.Dispatch<React.SetStateAction<boolean>> | undefined;
  setQueryData?: React.Dispatch<React.SetStateAction<any>> | undefined;
  notifyCompleted?: () => void;
};

export const UtilityUsageOverMonth = ({
  utility,
  unit,
  selectedBuilding = "",
  utilityName = "",
  defaultClient = "",
  defaultBuilding = "",
  widget = false,
  queryData,
  setQueryLoading = undefined,
  notifyCompleted,
  setQueryData,
}: UtilityUsageOverMonthProps) => {
  const { client: favClient } = useAppSelector(
    (state) => state.apiData.favourite
  );
  const now = new Date();
  const [fontSizeSm] = useToken("fontSizes", ["sm"]);

  const toDate = subDays(now, 1);
  const fromDate = addMinutes(startOfMonth(subMonths(toDate, 5)), 3600); //3600 to skip prev month

  const [data, setData] = useState<Array<{ time: string; Usage: number }>>([]);
  const tooltipBg = WidgetBackground();

  const client = defaultClient ? defaultClient : favClient;
  const building = defaultBuilding ? defaultBuilding : selectedBuilding;

  const {
    data: energyData,
    isLoading,
    isFetching,
  } = useGetEnergyOverTimeQuery(
    {
      client,
      filters: {
        utility,
        building,
        dateFrom: getUnixTime(endOfDay(fromDate!)),
        dateTo: getUnixTime(endOfDay(toDate!)),
      },
    },
    {
      skip: fromDate === undefined || toDate === undefined,
    }
  );

  useEffect(() => {
    if (!isLoading && !isFetching && energyData?.status === "success") {
      setData([]);
      setQueryLoading && setQueryLoading(false);

      const tempData: IUtilityMonthUsage = {};
      Object.keys(energyData["usageByDate"]).map((key) => {
        const tempDate = new Date(+key * 1000);
        const dateKey = format(tempDate, "MM/yyyy");
        if (tempData[dateKey] === undefined) {
          tempData[dateKey] = { date: tempDate, total: 0 };
        } else {
          tempData[dateKey].total += energyData["usageByDate"][key];
        }
      });
      sortBy(tempData, ["date"]).map((d, i) => {
        let Projection = 0;
        const tempArr = sortBy(tempData, ["date"]);
        if (tempArr.length - 1 === i) {
          const now = new Date();
          const p = (100 * +format(now, "d")) / +getDaysInMonth(now);
          Projection = (100 * d.total) / p;
        } else {
          Projection = d.total;
        }
        setData((prevState) => [
          ...prevState,
          { time: format(d.date, "MM/yyyy"), Usage: +d.total, Projection },
        ]);
      });
      if (setQueryData) {
        if (!isEqual(queryData, sortBy(tempData, ["date"]))) {
          setQueryData(sortBy(tempData, ["date"]));
        }
      }
    }
    notifyCompleted && notifyCompleted();
  }, [
    energyData,
    isLoading,
    isFetching,
    queryData,
    setQueryData,
    notifyCompleted,
    setQueryLoading,
  ]);

  if (utility === "") {
    return <></>;
  }
  if (isLoading || isFetching) {
    return <Loader />;
  }
  return (
    <Flex w="full" direction="column" p={widget ? 3 : 0}>
      {!widget ? (
        <GraphTitle
          text={`${utilityName} Usage Over Last 6 Months`}
          widget={widget}
        />
      ) : (
        <></>
      )}
      <ResponsiveContainer width="100%" height={widget ? "100%" : 400}>
        <ComposedChart margin={{ left: 50 }} data={data}>
          <Bar
            tooltipType="none"
            barSize={20}
            dataKey="Usage"
            stroke="#FFD400"
            fill="url(#energyWaveBar)"
          />
          <Line
            type="monotone"
            strokeWidth={chartStrokeWidth}
            dataKey="Projection"
            stroke="#F6AD55"
            strokeDasharray="3 3"
          />

          <Area
            strokeWidth={chartStrokeWidth}
            type="monotone"
            dataKey="Usage"
            fill="url(#energyWaveBar)"
            stroke="#FFD400"
          />

          <defs>
            <linearGradient id="energyWaveBar" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#FFCD41" stopOpacity={1} />
              <stop offset="95%" stopColor="#FFCD41" stopOpacity={0} />
            </linearGradient>
          </defs>

          <CartesianGrid strokeDasharray="5 5" />

          <Tooltip
            cursor={{ fill: "transparent" }}
            contentStyle={{
              backgroundColor: tooltipBg,
              borderRadius: "5px",
              fontSize: fontSizeSm,
            }}
            offset={10}
            formatter={(value, name) => {
              try {
                const v = +value;
                return (
                  Number(v).toLocaleString("en-US", {
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 2,
                  }) +
                  " " +
                  unit
                );
              } catch (e) {
                return value;
              }
            }}
          />
          {!widget ? <Brush dataKey="time" height={30} /> : <></>}
          <YAxis
            fontSize={fontSizeSm}
            unit={unit}
            dataKey={"Usage"}
            type="number"
            domain={[
              (_dataMin: number) => 0,
              (dataMax: number) => dataMax + dataMax * 0.05,
            ]}
            orientation="left"
            tickFormatter={(value) => {
              if (value === 0) {
                return "0";
              }
              return Number(value).toLocaleString("en-US", {
                maximumFractionDigits: 0,
              });
            }}
          ></YAxis>
          <XAxis
            fontSize={fontSizeSm}
            tickSize={10}
            tickMargin={5}
            dataKey="time"
            interval={"preserveStartEnd"}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Flex>
  );
};

type CarbonFootprintChartProps = {
  fromDate?: Date | undefined;
  toDate?: Date | undefined;
  utility: string | undefined;
  kgCo2: number;
  selectedBuilding: string;
  unit: string;
  widget?: boolean;
  defaultClient?: string;
  defaultBuilding?: string;
  carbonData?: Array<{ time: string; Usage: number }>;
  setQueryLoading?: React.Dispatch<React.SetStateAction<boolean>> | undefined;
  notifyCompleted?: () => void;
  setCarbonData?:
    | React.Dispatch<
        React.SetStateAction<Array<{ time: string; Usage: number }>>
      >
    | undefined;
};

export const CarbonFootprintChart = ({
  utility = "",
  selectedBuilding = "",
  defaultClient = "",
  defaultBuilding = "",
  kgCo2 = 0.265,
  widget = false,
  carbonData = [],
  setQueryLoading = undefined,
  notifyCompleted,
  setCarbonData = undefined,
}: CarbonFootprintChartProps) => {
  const { client: favClient } = useAppSelector(
    (state) => state.apiData.favourite
  );
  const [fontSizeSm] = useToken("fontSizes", ["sm"]);
  const [data, setData] = useState<Array<{ time: string; Usage: number }>>([]);
  const tooltipBg = WidgetBackground();
  const now = new Date();
  const toDate = subDays(now, 1);
  const fromDate = addMinutes(startOfMonth(subMonths(toDate, 5)), 3600); //3600 to skip prev month
  const [max, setMax] = useState(0);

  const client = defaultClient ? defaultClient : favClient;
  const building = defaultBuilding ? defaultBuilding : selectedBuilding;

  const {
    data: energyData,
    isLoading,
    isFetching,
  } = useGetEnergyOverTimeQuery(
    {
      client,
      filters: {
        utility,
        building,
        dateFrom: getUnixTime(endOfDay(fromDate!)),
        dateTo: getUnixTime(endOfDay(toDate!)),
      },
    },
    {
      skip: fromDate === undefined || toDate === undefined,
    }
  );

  useEffect(() => {
    if (!isLoading && !isFetching && energyData?.status === "success") {
      setData([]);
      setQueryLoading && setQueryLoading(false);

      const tempData: IUtilityMonthUsage = {};
      Object.keys(energyData["usageByDate"]).map((key) => {
        const tempDate = new Date(+key * 1000);
        const dateKey = format(tempDate, "MM/yyyy");
        if (tempData[dateKey] === undefined) {
          tempData[dateKey] = { date: tempDate, total: 0 };
        } else {
          tempData[dateKey].total += energyData["usageByDate"][key];
        }
      });
      Object.keys(tempData).map((key) => {
        tempData[key].total = tempData[key].total * kgCo2;
      });

      sortBy(tempData, ["date"]).map((d, i) => {
        let Projection = 0;

        if (Object.keys(tempData).length - 1 === i) {
          const p =
            (100 * +format(new Date(), "d")) / getDaysInMonth(new Date());

          Projection = (100 * +d.total) / p;
        } else {
          Projection = +d.total;
        }

        if (Projection > max) {
          setMax(Projection);
        }
        if (+d.total > max) {
          setMax(+d.total);
        }

        setData((prevState) => [
          ...prevState,
          {
            time: format(d.date, "MM/yyyy"),
            Usage: +d.total,
            Projection,
          },
        ]);
      });
    }
    notifyCompleted && notifyCompleted();
  }, [
    energyData,
    isLoading,
    isFetching,
    max,
    kgCo2,
    notifyCompleted,
    setQueryLoading,
  ]);

  useEffect(() => {
    if (data.length > 0 && carbonData.length === 0) {
      setCarbonData && setCarbonData(data);
    }
  }, [data, carbonData, setCarbonData]);

  if (utility === "") {
    return <></>;
  }
  if (isLoading || isFetching) {
    return <Loader />;
  }

  function getMin(data, max) {
    let min = +max;

    data?.map((d) => {
      if (d?.Usage < min) min = d?.Usage;
      if (d?.Projection < min) min = d?.Projection;
    });
    if (min === 0) {
      return 0;
    }
    return min - min * 0.1;
  }

  if (kgCo2 === 0) {
    return <Flex w="full" direction="column" p={widget ? 3 : 0}></Flex>;
  }
  return (
    <Flex w="full" direction="column" p={widget ? 3 : 0}>
      {!widget ? <GraphTitle text="Decarbonisation" widget={widget} /> : <></>}
      <ResponsiveContainer width="100%" height={widget ? "100%" : 400}>
        <ComposedChart margin={{ left: 70 }} data={data}>
          <defs>
            <linearGradient id="carbonWave" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#48BB78" stopOpacity={0.5} />
              <stop offset="95%" stopColor="#48BB78" stopOpacity={0} />
            </linearGradient>
          </defs>

          <CartesianGrid strokeDasharray="5 5" />
          <Bar
            tooltipType="none"
            dataKey="Usage"
            barSize={20}
            fill="#48BB7890"
          />
          <Line
            type="monotone"
            dot={false}
            strokeWidth={chartStrokeWidth}
            dataKey="Projection"
            stroke="#48BB78"
            strokeDasharray="3 3"
          />

          <Area
            strokeWidth={chartStrokeWidth}
            type="monotone"
            dataKey="Usage"
            fill="url(#carbonWave)"
            stroke="#48BB78"
          />

          <YAxis
            fontSize={fontSizeSm}
            allowDataOverflow={false}
            unit={"kg" + "CO2"}
            dataKey={"Projection"}
            // type="number"
            // domain={['dataMin', 'dataMin']}

            type="number"
            domain={[getMin(data, max), "dataMax + 1000"]}
            orientation="left"
            tickFormatter={(value) => {
              if (value === 0) {
                return "0";
              }
              return Number(value).toLocaleString("en-US", {
                maximumFractionDigits: 0,
              });
            }}
          ></YAxis>
          <XAxis
            fontSize={fontSizeSm}
            tickSize={10}
            tickMargin={5}
            dataKey="time"
          />

          <Tooltip
            contentStyle={{ backgroundColor: tooltipBg, borderRadius: "5px" }}
            offset={10}
            labelFormatter={(timeStr) => {
              return ""; //Hide date
            }}
            formatter={(value, name, x, index) => {
              // if (index === 1) return ''

              try {
                const v = +value;
                return (
                  Number(v).toLocaleString("en-US", {
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 2,
                  }) +
                  " " +
                  "kgCO2"
                );
              } catch (e) {
                return value;
              }
            }}
          />
          {!widget ? (
            <Brush style={{ marginTop: "20px" }} dataKey="time" height={30} />
          ) : (
            <></>
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </Flex>
  );
};

type CarbonFootprintTargetChartProps = {
  utility: IUtility;
  building: string;
  widget?: boolean;
  kwhOnly?: boolean;
  overwrittenTitle?: string;
  carbonData?: Array<{ time: string; Usage: number }>;
  notifyCompleted?: () => void;
  setCarbonData?:
    | React.Dispatch<
        React.SetStateAction<Array<{ time: string; Usage: number }>>
      >
    | undefined;
};

export const CarbonFootprintTargetChart = ({
  building,
  utility,
  carbonData,
  widget = false,
  overwrittenTitle = "",
  kwhOnly = false,
  notifyCompleted,
  setCarbonData,
}: CarbonFootprintTargetChartProps) => {
  const { client } = useAppSelector((state) => state.apiData.favourite);
  const { buildings } = useAppSelector((state) => state.apiData);
  const [data, setData] = useState<IUtilityMonthCarbonUsage[]>([]);
  const [utilityDate, setUtilityDate] = useState<Date | undefined>(undefined);
  const [max, setMax] = useState<number>(0);
  const [fromDate, setFromDate] = useState<Date | undefined>(undefined);
  const [toDate, setToDate] = useState<Date | undefined>(undefined);

  const {
    data: energyData,
    isLoading,
    isFetching,
  } = useGetEnergyOverTimeQuery(
    {
      client,
      filters: {
        utility: utility.id!,
        building,
        dateFrom: getUnixTime(endOfDay(fromDate!)),
        dateTo: getUnixTime(endOfDay(toDate!)),
      },
    },
    {
      skip: fromDate === undefined || toDate === undefined,
    }
  );

  const [fontSizeSm] = useToken("fontSizes", ["sm"]);
  const findBuilding = buildings.find((b) => b.id == building);
  const unit = kwhOnly ? "kWh" : "kgCO2";
  const multiplyBy = kwhOnly ? 1 : +utility.kgCo2;
  const lineColor = kwhOnly ? "#FFD400" : "#48BB78";
  const target = kwhOnly
    ? +findBuilding.targetKwh!
    : +findBuilding.targetKgCo2!;

  useEffect(() => {
    const now = new Date();
    let tempDate = new Date();
    tempDate.setHours(0, 0, 0, 0);
    tempDate.setDate(1);
    tempDate.setMonth(+utility.startingMonth);
    if (utilityDate === undefined) {
      setUtilityDate(tempDate);
    }

    const tempData: IUtilityMonthCarbonUsage[] = [];
    for (let i = 0; i < 12; i++) {
      const dateKey = format(tempDate, "MM/yyyy");
      tempData.push({
        date: tempDate!,
        total: 0,
        projection: undefined,
        dateKey,
      });
      tempDate = addMonths(tempDate, 1);
    }
    const sortedData = sortBy(tempData, ["date"]);

    let tempToDate = sortedData[11].date;
    tempToDate = lastDayOfMonth(tempToDate);
    tempToDate.setHours(23, 59, 59, 999);

    setFromDate(sortedData[0].date);
    setToDate(subDays(now, 1));

    if (!isLoading && !isFetching && energyData?.status === "success") {
      //Get totals for each months
      sortedData.map((d) => {
        let total = 0;
        Object.keys(energyData.usageByDate).map((key) => {
          const tempDate = new Date(+key * 1000);
          if (isSameMonth(tempDate, d.date) && isSameYear(tempDate, d.date)) {
            total = total + +energyData.usageByDate[key];
          }
        });

        if (total > 0) {
          d.total = +total * multiplyBy;
        } else {
          //Hide months with no data
          d.total = undefined;
          d.subTotal = undefined;
        }
      });
    }

    //Get subtotals for each month
    for (let i = 0; i < sortedData.length; i++) {
      const value = sortedData[i].total;
      if (value > 0 || value !== undefined) {
        let subTotal = 0;
        for (let j = 0; j < i + 1; j++) {
          if (sortedData[j].total !== undefined) {
            subTotal += +sortedData[j].total;
          }
        }
        sortedData[i].subTotal = +subTotal;
        sortedData[i].projection = +subTotal;
      }
    }

    for (let i = 0; i < sortedData.length; i++) {
      const value = sortedData[i].total;
      if (value === undefined) {
        let projectionTemp = 0;
        for (let j = 0; j < i + 1; j++) {
          if (sortedData[j].subTotal !== undefined) {
            projectionTemp += +sortedData[j].subTotal;
          } else {
            // projectionTemp += +sortedData[j].projection
            // ?
          }
        }

        if (projectionTemp > 0) {
          const projectionTempV =
            sortedData[i - 1].subTotal !== undefined
              ? sortedData[i - 1].subTotal
              : sortedData[i - 1].projection;
          sortedData[i].projection = +projectionTemp / i + projectionTempV;
          sortedData[i].total = +projectionTemp / i;
        } else {
          sortedData[i].projection = 0;
          sortedData[i].total = 0;
        }
      }
    }
    setData(sortedData);
    // setCarbonData && setCarbonData(sortedData);
    notifyCompleted && notifyCompleted();
  }, [
    utilityDate,
    utility.startingMonth,
    data.length,
    isLoading,
    energyData,
    isFetching,
    multiplyBy,
    notifyCompleted,
  ]);

  useEffect(() => {
    if (!isEqual(carbonData, data)) {
      setCarbonData && setCarbonData(data);
    }
  }, [data, carbonData, setCarbonData]);

  if (data.length === 0 || findBuilding === undefined) return <></>;

  if (isLoading || isFetching) return <Loader />;

  return (
    <Flex direction="column" w="full" h="full" p={widget ? 5 : 0}>
      <GraphTitle
        text={
          overwrittenTitle.length > 0
            ? overwrittenTitle
            : "Decarbonisation annual target pathway"
        }
        widget={widget}
      />
      <Flex w="full" h="full" pr={5} pt={2}>
        <ResponsiveContainer width="100%" height={widget ? "100%" : 400}>
          <ComposedChart margin={{ left: 70 }} data={data}>
            <XAxis
              fontSize={fontSizeSm}
              tickSize={10}
              tickMargin={5}
              dataKey="dateKey"
            />
            <Tooltip
              formatter={(v) => {
                try {
                  const value = Number(v).toLocaleString("en-GB", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  });
                  return value + " " + unit;
                } catch (e) {
                  return v;
                }
              }}
            />
            <Line
              isAnimationActive={false}
              dot={false}
              connectNulls={false}
              type="monotone"
              strokeWidth={chartStrokeWidth}
              strokeDasharray={"3 3"}
              dataKey="projection"
              name="Projection"
              stroke={lineColor}
            />
            <Line
              tooltipType="none"
              strokeWidth={chartStrokeWidth}
              isAnimationActive={false}
              dot={false}
              connectNulls={false}
              type="monotone"
              dataKey="subTotal"
              stroke={lineColor}
            />
            <YAxis
              fontSize={fontSizeSm}
              unit={unit}
              type="number"
              domain={[
                0,
                max > +findBuilding.targetKgCo2
                  ? max
                  : +findBuilding.targetKgCo2!,
              ]}
              tickFormatter={(value) => {
                if (value === 0) {
                  return "0";
                }
                return Number(value).toLocaleString("en-US", {
                  maximumFractionDigits: 0,
                });
              }}
            />
            {+target > 0 ? (
              <ReferenceLine
                strokeWidth={chartStrokeWidth}
                ifOverflow="visible"
                stroke={kwhOnly ? "#F6AD55" : "#276749"}
                strokeDasharray="5 5"
                segment={[
                  { x: data[0].dateKey, y: 0 },
                  { x: data[11].dateKey, y: target },
                ]}
              />
            ) : (
              <></>
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </Flex>
    </Flex>
  );
};
